// utils/extractIntent.ts

export type Intent =
  | "cost"
  | "ai_builder"
  | "general"
  | "builder"
  | "platform"
  | "niche"
  | "agency"
  | "research"
  | "other";

type WeightedPattern = { re: RegExp; weight: number };

type IntentConfig = {
  key: Intent;
  patterns: WeightedPattern[];
};

// Helper to normalize input a bit (keeps words and spaces)
function normalize(s: string): string {
  // Only keep basic Latin letters, numbers, and whitespace
  return s.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}

// ---- Pattern library (adjust anytime) ----
const INTENT_PATTERNS: IntentConfig[] = [
  {
    key: "cost",
    patterns: [
      { re: /\b(app|mobile|software)\b.*\bcost(s)?\b/i, weight: 3 },
      { re: /\bcost(s)?\b.*\b(app|mobile|software)\b/i, weight: 3 },
      { re: /\bhow\s+much\b/i, weight: 2 },
      { re: /\b(price|pricing)\b/i, weight: 2 },
      { re: /\bcost.*develop(ment)?\b/i, weight: 2 },
    ],
  },
  {
    key: "ai_builder",
    patterns: [
      { re: /\bai\b.*\b(create|build|generator|builder|creator)\b/i, weight: 3 },
      { re: /\b(build|create)\b.*\bapp\b.*\bai\b/i, weight: 3 },
      { re: /\bai\s+(app|software)\s+development\b/i, weight: 2 },
      { re: /\bai\s+(development|services|tools|company|companies)\b/i, weight: 2 },
      { re: /\bai\s+app\s+(generator|builder)\b/i, weight: 2 },
      { re: /\bwith\s+ai\b|\bai[-\s]?powered\b/i, weight: 1 },
      { re: /\bfree\b.*\b(ai|builder)\b/i, weight: 1 },
    ],
  },
  {
    key: "platform",
    patterns: [
      { re: /\bios\b|\biphone\b/i, weight: 3 },
      { re: /\bios\s+(apps?|applications?)\s+development\b/i, weight: 3 },
      { re: /\bmobile\s+(phone\s+)?application\s+development\b/i, weight: 2 },
      { re: /\bmobile\s+app\s+developer\b/i, weight: 2 },
    ],
  },
  {
    key: "niche",
    patterns: [
      { re: /\bfitness\s+app\b/i, weight: 3 },
      { re: /\bexpense(s)?\s+(tracker|tracking)\b/i, weight: 3 },
      { re: /\bcontractor\b.*\bcalculator\b/i, weight: 3 },
    ],
  },
  {
    key: "builder",
    patterns: [
      { re: /\bapp\s+builder(s)?\b/i, weight: 3 },
      { re: /\bno[-\s]?code\b/i, weight: 3 },
      { re: /\b(create|make)\s+(my\s+own\s+)?app\b/i, weight: 2 },
      { re: /\bbuild\s+an?\s+app\s+(software)?\b/i, weight: 2 },
      { re: /\b(app\s+development\s+)?platform\b/i, weight: 1 },
      { re: /\bsoftware\s+to\s+build\s+apps\b/i, weight: 2 },
    ],
  },
  {
    key: "agency",
    patterns: [
      { re: /\b(software|app)\s+development\s+company\b/i, weight: 3 },
      { re: /\bcustom\s+application\s+development\s+company\b/i, weight: 3 },
      { re: /\b(app|digital)\s+(agency|agencies)\b/i, weight: 2 },
      { re: /\bcompanies\s+that\s+build\s+apps\b/i, weight: 2 },
      { re: /\bindependent\s+app\s+developer\b/i, weight: 2 },
      { re: /\bmobile\s+app\s+companies\s+india\b|\bagency\s+in\s+india\b/i, weight: 2 },
      { re: /\busa\b|\bindia\b/i, weight: 1 }, // geo hints
    ],
  },
  {
    key: "general",
    patterns: [
      { re: /\bapp\s+development\b/i, weight: 3 },
      { re: /\b(website\s+and\s+)?app\s+development\b/i, weight: 2 },
      { re: /\b(build|create|make)\b.*\bapp\b/i, weight: 2 },
      { re: /\bbuilding\s+apps\s+software\b/i, weight: 1 },
    ],
  },
  {
    key: "research",
    patterns: [
      { re: /\bsoftware\s+tools\b/i, weight: 2 },
      { re: /forrester\s+wave/i, weight: 4 },
      { re: /\blow[-\s]?code\b/i, weight: 2 },
    ],
  },
];

// Tie-breaker priority if scores tie (leftmost wins)
const INTENT_PRIORITY: Intent[] = [
  "niche",
  "cost",
  "ai_builder",
  "platform",
  "agency",
  "builder",
  "general",
  "research",
  "other",
];

export function extractIntent(
  term: string | undefined | null,
  opts?: { minScore?: number; debug?: boolean }
): { intent: Intent; score: number; details?: Record<Intent, { score: number; hits: string[] }> } {
  if (!term) return { intent: "other", score: 0 };

  const q = normalize(term);
  const minScore = opts?.minScore ?? 2;

  const scores: Record<Intent, { score: number; hits: string[] }> = {
    cost: { score: 0, hits: [] },
    ai_builder: { score: 0, hits: [] },
    general: { score: 0, hits: [] },
    builder: { score: 0, hits: [] },
    platform: { score: 0, hits: [] },
    niche: { score: 0, hits: [] },
    agency: { score: 0, hits: [] },
    research: { score: 0, hits: [] },
    other: { score: 0, hits: [] },
  };

  for (const { key, patterns } of INTENT_PATTERNS) {
    for (const { re, weight } of patterns) {
      if (re.test(q)) {
        scores[key].score += weight;
        if (opts?.debug) scores[key].hits.push(String(re));
      }
    }
  }

  // Pick best by score, break ties by INTENT_PRIORITY
  let best: Intent = "other";
  let bestScore = 0;

  for (const intent of INTENT_PRIORITY) {
    const s = scores[intent].score;
    if (s > bestScore) {
      bestScore = s;
      best = intent;
    }
  }

  // Threshold to avoid overfitting weak matches
  if (bestScore < minScore) best = "other";

  return opts?.debug
    ? { intent: best, score: bestScore, details: scores }
    : { intent: best, score: bestScore };
}

const intentToHeadline: Record<string, string> = {
  // 1. Cost / Pricing
  cost: "Get clarity on MVP scope, timeline, and cost before you start building.",

  // 2. AI / Automation Builders
  ai_builder: "Have an app idea but no spec? Turn it into a build-ready plan in minutes.",

  // 3. General App Development
  general: "Move from idea to execution with a structured MVP plan and clear next steps.",

  // 4. App Builders / No-Code
  builder: "Outgrowing no-code? Get a pragmatic roadmap for a production-ready MVP.",

  // 5. Platform-Specific (iOS / Mobile)
  platform: "Planning iOS first and Android next? Get a phased build plan with delivery clarity.",

  // 6. Niche / Vertical Apps
  niche: "Building for a specific market? Get a tailored MVP plan grounded in real execution.",

  // 7. Agencies / Companies
  agency: "Need a credible plan before choosing a team? Start with clear scope and milestones.",

  // 8. Software Tools / Research
  research: "Comparing tools or platforms? Anchor decisions in a concrete build plan first.",

  // Default / Catch-All
  other: "Turn your app idea into a build-ready plan with clear MVP scope and timeline.",
};

export function generateHeadline(term: string | undefined | null): string {
  const { intent } = extractIntent(term);
  return intentToHeadline[intent] || intentToHeadline["other"];
}

// Example usage:
// console.log(generateHeadline("How much does it cost to build an iOS app with AI features?"));
// Output: "Get clarity on MVP scope, timeline, and cost before you start building."
